import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Download, Mic, Image as ImageIcon, FileText, Film, Mail } from "lucide-react";

const KIND_ICON = { logo: ImageIcon, photo: ImageIcon, doc: FileText, video: Film };

export default function Press() {
    const [assets, setAssets] = useState([]);
    const [form, setForm] = useState({ name: "", outlet: "", email: "", phone: "", deadline: "", message: "" });
    const [loading, setLoading] = useState(false);

    useEffect(() => { api.get("/press-assets").then((r) => setAssets(r.data)); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/media-inquiries", form);
            toast.success("Inquiry sent. Media relations will reply shortly.");
            setForm({ name: "", outlet: "", email: "", phone: "", deadline: "", message: "" });
        } catch (err) { toast.error(formatErr(err)); }
        finally { setLoading(false); }
    };

    return (
        <div data-testid="press-page" className="grain">
            <section className="border-b border-line py-16 md:py-24 bg-bg-soft">
                <div className="max-w-7xl mx-auto px-5 md:px-10">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase font-bold mb-4 flex items-center gap-3">
                        <Mic size={14} /> For Media
                    </div>
                    <h1 className="h-display text-6xl md:text-8xl">Press Kit</h1>
                    <p className="text-ink text-lg mt-5 max-w-xl">Logos, photos, fact sheets, and reels — cleared for editorial use.</p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 md:px-10 py-16">
                <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-6">Downloadables</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {assets.map((a, i) => {
                        const Icon = KIND_ICON[a.kind] || ImageIcon;
                        return (
                            <div key={a.id} className="card-flat overflow-hidden group" data-testid={`asset-${i}`}>
                                <div className="aspect-video bg-cover bg-center relative" style={{ backgroundImage: `url('${a.file_url}')` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70" />
                                    <div className="absolute top-3 left-3 bg-black/70 px-2 py-1 text-[10px] uppercase tracking-widest text-gold font-bold flex items-center gap-1">
                                        <Icon size={11} /> {a.kind}
                                    </div>
                                </div>
                                <div className="p-4">
                                    <div className="h-display text-base">{a.title}</div>
                                    {a.description && <p className="text-ink text-xs mt-2 leading-relaxed line-clamp-2">{a.description}</p>}
                                    <a
                                        href={a.file_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                        className="btn-ghost-gold mt-4 px-3 py-2 text-[10px] inline-flex items-center gap-2"
                                        data-testid={`asset-dl-${i}`}
                                    >
                                        <Download size={11} /> Download
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {assets.length === 0 && <div className="card-flat p-12 text-center text-ink">No assets yet.</div>}
            </section>

            <section className="max-w-5xl mx-auto px-5 md:px-10 pb-24">
                <div className="card-flat p-8 md:p-10" data-testid="media-inquiry-form">
                    <Mail className="text-gold" size={26} />
                    <h2 className="h-display text-3xl md:text-4xl mt-4">Media Inquiry</h2>
                    <p className="text-ink text-sm mt-3 leading-relaxed">Interview requests, credential applications, or general press questions.</p>
                    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-7">
                        <Field label="Your Name" required>
                            <input data-testid="mi-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field label="Outlet / Publication" required>
                            <input data-testid="mi-outlet" required value={form.outlet} onChange={(e) => setForm({ ...form, outlet: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field label="Email" required>
                            <input data-testid="mi-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field label="Phone (Optional)">
                            <input data-testid="mi-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <Field label="Deadline (Optional)">
                            <input data-testid="mi-deadline" placeholder="e.g. Friday EOD" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="w-full px-4 py-3 text-sm" />
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Message" required>
                                <textarea data-testid="mi-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 text-sm" />
                            </Field>
                        </div>
                        <div className="sm:col-span-2">
                            <button type="submit" disabled={loading} data-testid="mi-submit" className="btn-gold px-7 py-4 text-xs disabled:opacity-60">
                                {loading ? "Sending…" : "Send Inquiry"}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                {label} {required && <span className="text-gold">*</span>}
            </div>
            {children}
        </label>
    );
}
